// StudyAbroadPricing.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const StudyAbroadPricing = () => {
  const coverageItems = [
    'University and course shortlisting',
    'Application preparation and submission',
    'Statement of Purpose drafting and refinement',
    'Academic document coordination',
    'Admission follow ups and guidance'
  ];

  const importantNotes = [
    'Tuition fees are paid directly to the institution',
    'Notapaths does not collect tuition payments',
    'Scholarship or tuition discounts are issued directly by institutions',
    'Admission decisions are made by schools, not Notapaths. However, 98% of those who follow our guidelines and use our application support succeed. We\'re privileged to work closely with admission boards to discuss and align your application with their requirements, hence our exceptional success rate.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
              Study Abroad Pricing Structure
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#F5F8FA] p-8 rounded-lg mb-6"
          >
            <h3 className="text-xl font-semibold text-[#202356] mb-4">
              Study Abroad Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              Study Abroad service fees vary depending on destination, number of applications, and level of support required.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Our Study Abroad fees typically cover:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-[#202356] mb-4">
                Transparent Pricing Structure
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#202356]/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-[#202356]">Base Service Fee</p>
                    <p className="text-sm text-gray-600">Complete application support for one university</p>
                  </div>
                  <p className="text-2xl font-bold text-[#202356]">$149</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-base font-medium text-gray-700">Additional Applications</p>
                    <p className="text-sm text-gray-600">Per extra university application</p>
                  </div>
                  <p className="text-xl font-semibold text-gray-700">+$20</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Example:</span> Applying to 3 universities = $149 + ($20 × 2) = <span className="font-semibold text-[#202356]">$189 total</span>
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Final pricing is confirmed after consultation based on your profile and chosen pathway.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-[#202356]">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StudyAbroadPricing;
